import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// Constants
import {
  ACCOUNT_TYPE_OPTIONS,
  ACCOUNT_STATUS_OPTIONS,
  NATIONAL_ID_TYPE_OPTIONS,
  BOOLEAN_OPTIONS,
  // ACTIVITY_ENTITY_OPTIONS
} from 'common/constants/formOptions'
import { COUNTRIES_LIST } from 'common/constants/countries'

// Store
import { actions } from 'core/store'

// Styled Elements
import {
  FormWrapper,
  FormInputGroupItem,
  FormGroupWrapper,
  TableButtonWrapper,
  TableRowWrapper,
  TablePanelWrapper,
} from './AccountEntityDetailsForm.elements'

// Views
import {
  Form,
  FormTextField,
  FormSearchSelect,
  FormSelect,
  FormDatePicker,
  FormTextArea,
  AccordionTableItem,
} from 'views/components'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function AccountEntityDetailsForm(props) {
  // Destructure
  const { values, viewMode, formRef, onSubmit } = props

  // Internal State
  const [initialData, setInitialData] = useState()
  // Forms
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true)
  const [isAddressExpanded, setIsAddressExpanded] = useState(false)
  const [isFreeFormAddressExpanded, setIsFreeFormAddressExpanded] = useState(false)
  const [isNationalIDExpanded, setIsNationalIDExpanded] = useState(false)

  // Variables
  const validationSchema = Yup.object().shape({
    reference_id: Yup.string().max(50).required('Reference ID is required'),
    customer_id: Yup.string()
      .max(50)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      ),
    email: Yup.string().email().nullable(),
    name_legal: Yup.string().max(100).required('Legal Name is required'),
    name_business: Yup.string().max(100).nullable(),
    is_customer: Yup.bool().required('Specify if account is a customer'),
    registration_country_code: Yup.string().max(2).nullable(),
    address_line: Yup.string()
      .max(256)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test(
        'address_line',
        'Required if street number, street name, and building name are not specified',
        (value, context) => hasRequiredAddressFields(context)
      ),
    address_street_no: Yup.string()
      .max(10)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('address_street_no', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context)
      ),
    address_street_name: Yup.string()
      .max(100)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('address_street_name', 'Required with building name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context)
      ),
    address_building_name: Yup.string()
      .max(20)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('address_building_name', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context)
      ),
    address_floor: Yup.string().max(20).nullable(),
    address_room: Yup.string().max(20).nullable(),
    address_department: Yup.string().max(20).nullable(),
    address_postbox: Yup.string().max(20).nullable(),
    address_city_location: Yup.string().max(50).nullable(),
    address_city: Yup.string()
      .max(50)
      .nullable()
      .test('address_city', 'Required if address fields are defined', (value, context) => hasAddress(value, context)),
    address_region: Yup.string().max(50).nullable(),
    address_district: Yup.string().max(50).nullable(),
    address_postcode: Yup.string().max(25).nullable(),
    address_country_code: Yup.string()
      .max(2)
      .nullable()
      .test('address_country_code', 'Required if address fields are defined', (value, context) =>
        hasAddress(value, context)
      ),
    national_id_type_code: Yup.string()
      .max(10)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('national_id_type_required', 'ID Type is required when National ID is provided', (value, context) => {
        const hasNationalId = Boolean(context.parent.national_id)

        // If any other National ID fields are provided, this field is required
        if (hasNationalId && !value) {
          return false
        }
        return true
      }),
    national_id: Yup.string()
      .max(35)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('national_id_required', 'National ID is required when ID Type is provided', (value, context) => {
        const hasIdType = Boolean(context.parent.national_id_type_code)

        // If any other National ID fields are provided, this field is required
        if (hasIdType && !value) {
          return false
        }
        return true
      }),
    national_id_issue_country: Yup.string()
      .max(2)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('issue_country_not_required', 'Country Issuer must not be specified for entity accounts', (value) => {
        if (value) {
          return false
        }

        return true
      }),
    national_id_registration_authority: Yup.string()
      .max(10)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context)
      )
      .test('registration_authority_conditional', (value, context) => {
        const idType = context.parent.national_id_type_code

        // Skip validation if ID Type is not provided
        if (!idType) {
          return true
        }

        // For LEIX type, registration authority must NOT be provided
        if (idType === 'LEIX') {
          if (value) {
            return context.createError({
              path: context.path,
              message: 'Registration Authority must not be specified for national ID type "LEIX"',
            })
          }
          return true
        }

        // For all other ID types, registration authority is required
        if (!value) {
          return context.createError({
            path: context.path,
            message: `Registration Authority is required for national ID type "${idType}"`,
          })
        }

        return true
      }),
  })

  // Functions

  function hasRequiredAddressFields(context) {
    const parent = context?.parent

    /*
      If the form already has required account fields
      (date and place of birth, customer id, or national id),
      skip required address fields validation
    */
    const hasRequiredAccountFields = validateRequiredAccountFields(context)
    if (hasRequiredAccountFields) {
      return true
    }

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasAddress = hasAddressLine || (hasStreetName && (hasStreetNo || hasBuildingName))

    return hasAddress
  }

  function hasAddress(value, context) {
    const parent = context?.parent

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)

    // If no address info is provided, value is optional
    if (!hasAddressLine && !hasStreetNo && !hasStreetName && !hasBuildingName) {
      return true
    }

    // If we have validated address information, value is required
    return Boolean(value)
  }

  /*
    Validates if at least one of:
    - National ID
    - Address
    - Customer ID
    is provided.

    If an attempt to provide one of these identifiers is made,
    even if the identifier is incomplete
    (e.g. specifying incomplete address, national ID, or date and place of birth),
    the validation will pass.
  */
  function validateRequiredAccountFields(context) {
    const parent = context?.parent

    // Check if any national ID fields are provided
    // The validation here is more relaxed, since we also require the other field if one is provided
    const hasNationalIdType = Boolean(parent?.national_id_type_code)
    const hasNationalID = Boolean(parent?.national_id)
    const hasNationalIDIssueCountry = Boolean(parent?.national_id_issue_country)
    const hasNationalIDRegistrationAuthority = Boolean(parent?.national_id_registration_authority)

    const hasNationalIDInfo =
      hasNationalID || hasNationalIdType || hasNationalIDIssueCountry || hasNationalIDRegistrationAuthority

    // Check if customer ID is provided
    const hasCustomerID = Boolean(parent?.customer_id)

    // Check if any required address is provided
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasBuildingName = Boolean(parent?.address_building_name)

    const hasAddress = hasAddressLine || hasStreetName || hasStreetNo || hasBuildingName

    // Return true if at least one identifier method is provided
    return hasNationalIDInfo || hasAddress || hasCustomerID
  }

  // UseEffects
  useEffect(() => {
    // TODO REMOVE HARD CODED VALUE
    setInitialData({ ...values, address_type_code: 'GEOG' })
  }, [values])
  return (
    <FormWrapper>
      {initialData && (
        <Form formRef={formRef} initialValues={initialData} validationSchema={validationSchema} onSubmit={onSubmit}>
          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsDetailsExpanded(val)
            }}
            expandPanel={isDetailsExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isDetailsExpanded}>Business information</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Reference ID*" name="reference_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect disabled label="Account Type*" name="account_type_id" options={ACCOUNT_TYPE_OPTIONS} />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Customer ID*" name="customer_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect disabled={viewMode} label="Is Customer*" name="is_customer" options={BOOLEAN_OPTIONS} />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect disabled={viewMode} label="Status" name="status_id" options={ACCOUNT_STATUS_OPTIONS} />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Email" type="email" name="email" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Business Name" name="name_business" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Legal Name*" name="name_legal" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect
                      disabled={viewMode}
                      label="Registration Country"
                      name="registration_country_code"
                      options={COUNTRIES_LIST}
                    />
                  </FormInputGroupItem>
                  {/* <FormInputGroupItem>
              <FormDatePicker disabled={viewMode} label="Date of Incorporation" name="inc_date" />
            </FormInputGroupItem> */}
                  {/* <FormInputGroupItem>
              <FormSelect
                disabled={viewMode}
                label="Primary Activity"
                name="primary_activity_code"
                options={ACTIVITY_ENTITY_OPTIONS}
              />
            </FormInputGroupItem> */}

                  <FormInputGroupItem>
                    <FormDatePicker disabled label="Created" name="created" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormDatePicker disabled label="Last Modified" name="last_modified" />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />

          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsAddressExpanded(val)
            }}
            expandPanel={isAddressExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isAddressExpanded}>Structured address</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Street Number*" name="address_street_no" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Street Name*" name="address_street_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Building Name*" name="address_building_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Floor" name="address_floor" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Room" name="address_room" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Department" name="address_department" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Postbox" name="address_postbox" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="City Location" name="address_city_location" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="City*" name="address_city" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Region" name="address_region" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="District" name="address_district" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Zipcode" name="address_postcode" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect
                      disabled={viewMode}
                      label="Country*"
                      name="address_country_code"
                      options={COUNTRIES_LIST}
                    />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />

          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsFreeFormAddressExpanded(val)
            }}
            expandPanel={isFreeFormAddressExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isFreeFormAddressExpanded}>Free form address</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormTextArea disabled={viewMode} label="Address Line" name="address_line" />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />

          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsNationalIDExpanded(val)
            }}
            expandPanel={isNationalIDExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isNationalIDExpanded}>National ID</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormSelect
                      disabled={viewMode}
                      label="National ID Type"
                      name="national_id_type_code"
                      options={NATIONAL_ID_TYPE_OPTIONS}
                    />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="National ID" name="national_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField
                      disabled={viewMode}
                      label="National ID Registration Authority"
                      name="national_id_registration_authority"
                    />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />
        </Form>
      )}
    </FormWrapper>
  )
}

// Default Props
AccountEntityDetailsForm.defaultProps = {
  formRef: {},
  form: {},
  actions: {},
  values: {
    reference_id: '',
    account_type_id: 0,
    status_id: 0,
    email: '',
    name_business: '',
    name_legal: '',
    incorporation_country: '',
    inc_date: '',
    primary_activity_code: 0,

    created: '',
    last_modified: '',

    address_street_name: '',
    address_city: '',
    address_region: '',
    address_postcode: '',
    address_country_code: '',
  },
  viewMode: true,
}

// Proptypes Validation
AccountEntityDetailsForm.propTypes = {
  formRef: PropTypes.shape({ root: PropTypes.string }),
  form: PropTypes.shape({
    COUNTRIES_LIST: PropTypes.instanceOf(Array),
    toBeUpdatedAccount: PropTypes.shape({}),
  }),
  actions: PropTypes.shape({
    setCountryListOptions: PropTypes.func,
    setIsEntityAccountDrawerOpen: PropTypes.func,
    setToBeUpdatedAccount: PropTypes.func,
    setIsPageTableUpdated: PropTypes.func,
    showAlert: PropTypes.func,
  }),
  values: PropTypes.shape({
    reference_id: PropTypes.string,
    account_type_id: PropTypes.number,
    status_id: PropTypes.number,
    email: PropTypes.string,
    name_business: PropTypes.string,
    name_legal: PropTypes.string,
    incorporation_country: PropTypes.string,
    inc_date: PropTypes.string,
    primary_activity_code: PropTypes.number,

    created: PropTypes.string,
    last_modified: PropTypes.string,

    address_street_name: PropTypes.string,
    address_city: PropTypes.string,
    address_region: PropTypes.string,
    address_postcode: PropTypes.string,
    address_country_code: PropTypes.string,
  }),
  viewMode: PropTypes.bool,
  onSubmit: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountEntityDetailsForm)
